// User and Role type definitions for the admin system

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Record<string, string[]>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isVerified: boolean;
  emailVerifiedAt?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  role: Role;
}

export interface UserFormData {
  username: string;
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  roleId: string;
  isActive: boolean;
  isVerified: boolean;
}

export interface UserFilters {
  search?: string;
  role?: string;
  status?: 'active' | 'inactive';
  page?: number;
  limit?: number;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RoleFormData {
  name: string;
  description: string;
  permissions: Record<string, string[]>;
  isActive: boolean;
}

// =====================================================
// Role model — mirrors backend/constants/roles.js
// Authorization is ROLE-NAME based. The `permissions` JSONB on
// the Role is an informational label computed server-side from
// CAPABILITY_LABELS; it is never consulted for access control.
// Roles govern the /admin dashboard and admin APIs ONLY.
// =====================================================

// The 4 dashboard roles (most to least privileged).
export const ROLE_NAMES = [
  'super_admin',
  'admin',
  'moderator',
  'viewer'
] as const;

export type RoleName = typeof ROLE_NAMES[number];

// Access groups used by route guards and menu filtering.
// Must match backend/constants/roles.js ACCESS_GROUPS.
export const ACCESS_GROUPS = {
  SUPER_ADMIN_ONLY: ['super_admin'],
  USER_MANAGERS: ['super_admin', 'admin'],
  OPERATIONAL: ['super_admin', 'admin', 'moderator'],
} as const;

export type AccessGroup = (typeof ACCESS_GROUPS)[keyof typeof ACCESS_GROUPS];

// Dropped legacy roles — kept here only so old blobs can be
// recognized (they are remapped to the 4 roles server-side).
export const DROPPED_ROLES = [
  'coordinator',
  'field_worker',
  'reporter',
  'analyst',
  'support',
  'guest'
] as const;


